import { useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export function useTableSort<T extends object>(defaultSortField: keyof T | string) {
  const [sortField, setSortField] = useState<keyof T | string>(defaultSortField);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: keyof T | string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedData = (data: T[], customSort?: (a: T, b: T) => number) => {
    return [...data].sort((a, b) => {
      if (customSort) {
        return sortDirection === 'asc' ? customSort(a, b) : customSort(b, a);
      }

      if (typeof sortField !== 'string' || sortField in a) {
        const aValue = a[sortField as keyof T];
        const bValue = b[sortField as keyof T];

        if (aValue === undefined || bValue === undefined) return 0;
        if (aValue === null) return sortDirection === 'asc' ? -1 : 1;
        if (bValue === null) return sortDirection === 'asc' ? 1 : -1;

        if (aValue instanceof Date && bValue instanceof Date) {
          return sortDirection === 'asc' 
            ? aValue.getTime() - bValue.getTime()
            : bValue.getTime() - aValue.getTime();
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortDirection === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        return sortDirection === 'asc'
          ? (aValue < bValue ? -1 : 1)
          : (bValue < aValue ? -1 : 1);
      }

      return 0;
    });
  };

  return {
    sortField,
    sortDirection,
    handleSort,
    getSortedData
  };
} 